/**
 * T140 [US7] — Trading Agent tab bar.
 *
 * Terminal aesthetic (per FR-025 and tmp/platform-mockups/trading-agent.html):
 *   JetBrains Mono, uppercase, tight letter-spacing, no backdrop blur —
 *   all styling inherited from `.theme-trading-agent .app-tab` in tabs.css.
 *
 * Tabs (no Admin tab per FR-025 — platform admin lives under /admin):
 *   Dashboard · Trade · Strategies · Approvals · Discovery · History
 *
 * Desktop-only — the PWA TradingApp routes to separate pages and doesn't
 * need a secondary tab bar.
 *
 * Routes map to the existing `/apps/trading-agent/{dashboard,trading,...}`
 * pages. Strategies and Discovery resolve to placeholder routes — the
 * link is in place for when those pages ship; AppPlaceholder handles
 * unmatched children gracefully.
 */

import type { JSX } from 'react';

import { AppTab, AppTabs } from '@shared/platform/tabs';

export interface TradingAgentTabsProps {
  /** Root path — defaults to `/apps/trading-agent`. */
  basePath?: string;
  /**
   * Optional pending-approvals count rendered as a small trailing badge
   * on the Approvals tab. Leave undefined to hide the badge.
   */
  approvalsCount?: number;
  variant?: 'desktop' | 'mobile';
}

/* Lucide-free icons — keep the bundle lean. 13×13 to match the terminal
   mockup; strokeWidth 2 for the square, utilitarian look. */
function IconDashboard(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18M9 21V9" />
    </svg>
  );
}
function IconTrade(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}
function IconStrategies(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
    </svg>
  );
}
function IconApprovals(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  );
}
function IconDiscovery(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 22s-8-4.5-8-11.8A8 8 0 0 1 12 2a8 8 0 0 1 8 8.2c0 7.3-8 11.8-8 11.8Z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}
function IconHistory(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  );
}

export function TradingAgentTabs({
  basePath = '/apps/trading-agent',
  approvalsCount,
  variant = 'desktop',
}: TradingAgentTabsProps): JSX.Element {
  const trimmed = basePath.replace(/\/$/, '');

  return (
    <AppTabs variant={variant} aria-label="Trading Agent sections">
      <AppTab to={`${trimmed}/dashboard`} label="Dashboard" icon={<IconDashboard />} end />
      <AppTab to={`${trimmed}/trading`} label="Trade" icon={<IconTrade />} />
      <AppTab to={`${trimmed}/strategies`} label="Strategies" icon={<IconStrategies />} />
      <AppTab
        to={`${trimmed}/approvals`}
        label="Approvals"
        icon={<IconApprovals />}
        count={approvalsCount}
      />
      <AppTab to={`${trimmed}/discovery`} label="Discovery" icon={<IconDiscovery />} />
      <AppTab to={`${trimmed}/history`} label="History" icon={<IconHistory />} />
    </AppTabs>
  );
}

export default TradingAgentTabs;
